import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { CurrentViewTypeEnum, PointsBaseMap } from "./typings";
import { ContextMenu, ContextMenuItem, MarkersContainer } from "./styles";
import { GeometryType } from "./typings";
import useModalOrdemServicoMatriz from "../../../hooks/useModalOrdemServicoMatriz";
import { CustomFeature } from "core/features/maps/typings";
import { MdAddCircle, MdClear } from "react-icons/md";
import OrdemServico from "../../ModalOrdemServicoMatriz";
import useMapData from "../../../hooks/useMapData";
import { useTalhaoBoxSelect } from "./hooks/useTalhaoBoxSelect";
import { buildTalhaoBoundsIndex, TalhaoBounds } from "./hooks/boxSelectUtils";
import BoxSelectOverlay from "./components/BoxSelectOverlay";
import {
  SELECTED_STROKE,
  SELECTED_STROKE_WEIGHT,
  SELECTED_Z_INDEX,
} from "./hooks/talhaoSelectionColors";

interface BaseMapProps {
  mapRef: any;
  displayMarkers: boolean;
  currentViewType: CurrentViewTypeEnum;
  points?: PointsBaseMap;
  planoOperacionalData?: any;
}

function TalhaoLabels({
  map,
  items,
  visible,
  currentViewType,
}: {
  map: google.maps.Map | null;
  items: any[];
  visible: boolean;
  currentViewType: CurrentViewTypeEnum;
}) {
  useEffect(() => {
    if (!map || !visible) return;

    const canvas = document.createElement("canvas");
    canvas.style.cssText = "position:absolute;left:0;top:0;pointer-events:none;z-index:0;";
    const overlay = new google.maps.OverlayView();
    let frame = 0;

    const draw = () => {
      const div = map.getDiv();
      canvas.width = div.clientWidth;
      canvas.height = div.clientHeight;
      const context = canvas.getContext("2d");
      const projection = overlay.getProjection();
      if (!context || !projection) return;

      context.clearRect(0, 0, canvas.width, canvas.height);
      context.fillStyle = currentViewType === CurrentViewTypeEnum.box ? "#000" : "#fff";
      context.font = currentViewType === CurrentViewTypeEnum.box ? "14px Arial" : "bold 14px Arial";
      context.textAlign = "center";
      context.textBaseline = "middle";

      for (const item of items) {
        const lat = Number(item.properties?.pontoCentralLat);
        const lng = Number(item.properties?.pontoCentralLong);
        const text = item.properties?.codigo;
        if (!Number.isFinite(lat) || !Number.isFinite(lng) || !text) continue;

        const point = projection.fromLatLngToContainerPixel(
          new google.maps.LatLng(lat, lng)
        );
        if (
          !point ||
          point.x < -30 ||
          point.y < -20 ||
          point.x > canvas.width + 30 ||
          point.y > canvas.height + 20
        ) continue;

        if (currentViewType === CurrentViewTypeEnum.box) {
          context.beginPath();
          context.arc(point.x, point.y, 13, 0, Math.PI * 2);
          context.fillStyle = "#fff";
          context.fill();
          context.strokeStyle = "#64748b";
          context.lineWidth = 1;
          context.stroke();
          context.fillStyle = "#000";
        }

        context.fillText(String(text), point.x, point.y);
      }
    };

    overlay.onAdd = () => {
      map.getDiv().appendChild(canvas);
    };
    overlay.draw = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(draw);
    };
    overlay.onRemove = () => canvas.remove();
    overlay.setMap(map);

    const redraw = () => overlay.draw();
    const listeners = [
      map.addListener("bounds_changed", redraw),
      map.addListener("zoom_changed", redraw),
    ];
    return () => {
      cancelAnimationFrame(frame);
      listeners.forEach((listener) => listener.remove());
      overlay.setMap(null);
    };
  }, [map, items, visible, currentViewType]);

  return null;
}

const SELECTION_FILL = "#0078ff";
const AVAILABLE_FILL = "#E2E8F0";
const VINCULADO_FILLS = new Set(["#009157", "#F5F3CD", "#FFF3CD", "#CECECE"]);

function getFeatureTalhaoId(feature: google.maps.Data.Feature): string | null {
  const id = feature.getProperty("id");
  if (id == null || id === "") return null;
  return String(id);
}

function normalizeApiFill(fill: unknown): string | null {
  if (typeof fill !== "string" || !fill.trim()) return null;
  const value = fill.trim();
  if (value.toLowerCase() === "transparent") return null;
  return value;
}

function resolveFeatureFillColor(
  feature: google.maps.Data.Feature,
  ordemServicoId: string,
  ordemServicoColor: string,
  editChecked: boolean,
  activeEdit: boolean
): string {
  const id = feature.getProperty("id");
  const apiFill = normalizeApiFill(feature.getProperty("fill"));
  const ordemServico = feature
    .getProperty("ordemServicoIds")
    ?.includes(ordemServicoId);

  if (ordemServico && ordemServicoColor) return ordemServicoColor;
  if (apiFill && VINCULADO_FILLS.has(apiFill.toUpperCase())) return apiFill;
  if (id && editChecked && !activeEdit) return "transparent";
  if (id) return AVAILABLE_FILL;
  return apiFill ?? AVAILABLE_FILL;
}

function BaseMap({
  mapRef,
  displayMarkers,
  currentViewType,
  points,
  planoOperacionalData
}: BaseMapProps) {

  const setListTalhoesIds = useMapData((s) => s.setListTalhoesIds);

  const ordemServicoIdAndColor = useModalOrdemServicoMatriz((s) => s.ordemServicoIdAndColor);
  const ordemService = useModalOrdemServicoMatriz((s) => s.ordemService);
  const activeEdit = useModalOrdemServicoMatriz((s) => s.activeEdit);
  const editChecked = useModalOrdemServicoMatriz((s) => s.editChecked);
  const setOrdemService = useModalOrdemServicoMatriz((s) => s.setOrdemService);
  const setSelectedItem = useModalOrdemServicoMatriz((s) => s.setSelectedItem);
  const setEditChecked = useModalOrdemServicoMatriz((s) => s.setEditChecked);
  const setActiveEdit = useModalOrdemServicoMatriz((s) => s.setActiveEdit);
  const setOrdemServicoIdAndColor = useModalOrdemServicoMatriz((s) => s.setOrdemServicoIdAndColor);

  const selectedTalhoesRef = useRef<Set<string>>(new Set());
  const selectedCodesRef = useRef<Set<string>>(new Set());
  const selectedFeaturesRef = useRef<Map<string, google.maps.Data.Feature>>(new Map());
  const featuresByIdRef = useRef<Map<string, google.maps.Data.Feature>>(new Map());
  const boundsByIdRef = useRef<Map<string, TalhaoBounds>>(new Map());
  const talhaoCodigoByIdRef = useRef<Map<string, string>>(new Map());
  const styleFnRef = useRef<((feature: google.maps.Data.Feature) => google.maps.Data.StyleOptions) | null>(null);
  const syncListRafRef = useRef<number | null>(null);
  const syncListTimeoutRef = useRef<number | null>(null);

  const [popupPosition, setPopupPosition] = useState<google.maps.LatLngLiteral | null>(null);
  const [popupTalhao, setPopupTalhao] = useState<CustomFeature | null>(null);
  const [isOrdemServicoOpen, setIsOrdemServicoOpen] = useState(false);

  const ordemServicoId = ordemServicoIdAndColor.ordemServicoId;
  const ordemServicoColor = ordemServicoIdAndColor.color;
  const ordemTalhaoIds = ordemServicoIdAndColor.talhaoIds;

  const Polygons = useMemo(
    () =>
      points?.features.filter(
        (feature) =>
          feature.geometry.type === GeometryType.Polygon ||
          feature.geometry.type === GeometryType.MultiPolygon
      ) || [],
    [points]
  );

  const buildStyleFn = useCallback(() => {
    return (feature: google.maps.Data.Feature): google.maps.Data.StyleOptions => {
      const stroke = feature.getProperty("stroke");
      const strokeWidth = 2;
      const strokeOpacity = Number(feature.getProperty("strokeOpacity")) || 1;
      const talhaoId = getFeatureTalhaoId(feature);
      const fill = feature.getProperty("fill");
      const fillOpacity = Number(feature.getProperty("fillOpacity"));
      const isSelected =
        !!feature.getProperty("selected") &&
        !!talhaoId &&
        selectedTalhoesRef.current.has(talhaoId);

      const fillColor = resolveFeatureFillColor(
        feature,
        ordemServicoId,
        ordemServicoColor,
        editChecked,
        activeEdit
      );

      const isOrdemTalhao = talhaoId && ordemTalhaoIds?.includes(talhaoId);
      if (isSelected && talhaoId && !isOrdemTalhao) {
        return {
          fillColor: SELECTION_FILL,
          fillOpacity: 0.8,
          strokeColor: SELECTED_STROKE,
          strokeWeight: SELECTED_STROKE_WEIGHT,
          zIndex: SELECTED_Z_INDEX,
        };
      }

      if (
        talhaoId &&
        editChecked &&
        !activeEdit &&
        fillColor === "transparent"
      ) {
        return {
          fillColor: "transparent",
          fillOpacity: 0,
          strokeColor: stroke || "#000",
          strokeWeight: 2,
          zIndex: 1000,
        };
      }

      if (talhaoId) {
        return {
          fillColor,
          fillOpacity:
            fillColor === "transparent"
              ? 0
              : fillColor.toLowerCase() === "#fbbf24"
                ? 1
                : 0.8,
          strokeColor: stroke || "#000",
          strokeWeight: 2,
          zIndex: 1000,
        };
      }

      if (!fill) {
        return {
          strokeColor: stroke || "#000",
          strokeWeight: strokeWidth,
          strokeOpacity: strokeOpacity,
          fillOpacity: 0,
        };
      }

      return {
        strokeColor: stroke || "#000",
        strokeWeight: strokeWidth,
        strokeOpacity: strokeOpacity,
        fillColor: fill,
        fillOpacity: isNaN(fillOpacity) ? 0.6 : fillOpacity,
      };
    };
  }, [ordemServicoId, ordemServicoColor, ordemTalhaoIds, editChecked, activeEdit]);

  const rebuildFeatureIndex = useCallback((map: google.maps.Map) => {
    const next = new Map<string, google.maps.Data.Feature>();
    map.data.forEach((feature) => {
      const id = getFeatureTalhaoId(feature);
      if (id) next.set(id, feature);
    });
    featuresByIdRef.current = next;
  }, []);

  /** Só os talhões tocados — evita revertStyle+setStyle no mapa inteiro no box select. */
  const applyTouchedSelectionStyles = useCallback(
    (
      features: google.maps.Data.Feature[],
      options?: { syncSelectedProperty?: boolean }
    ) => {
      const map = mapRef.current;
      if (!map || features.length === 0) return;

      const syncSelectedProperty = options?.syncSelectedProperty ?? true;

      const applyOne = (feature: google.maps.Data.Feature) => {
        const talhaoId = getFeatureTalhaoId(feature);
        if (!talhaoId) return;

        try {
          const selected = selectedTalhoesRef.current.has(talhaoId);
          if (syncSelectedProperty) {
            feature.setProperty("selected", selected);
          }

          if (selected && !ordemTalhaoIds?.includes(talhaoId)) {
            map.data.overrideStyle(feature, {
              fillColor: SELECTION_FILL,
              fillOpacity: 0.8,
              strokeColor: SELECTED_STROKE,
              strokeWeight: SELECTED_STROKE_WEIGHT,
              zIndex: SELECTED_Z_INDEX,
            });
          } else {
            map.data.revertStyle(feature);
          }
        } catch {
          // Feature may have been removed on GeoJSON reload.
        }
      };

      for (const feature of features) applyOne(feature);
    },
    [mapRef, ordemTalhaoIds]
  );

  const revertSelectedFeatureStyles = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;

    selectedFeaturesRef.current.forEach((feature) => {
      try {
        feature.setProperty("selected", false);
        map.data.revertStyle(feature);
      } catch {
        // Feature may have been removed on GeoJSON reload.
      }
    });
  }, [mapRef]);

  const clearSelectionState = useCallback(() => {
    revertSelectedFeatureStyles();
    selectedTalhoesRef.current.clear();
    selectedCodesRef.current.clear();
    selectedFeaturesRef.current.clear();
  }, [revertSelectedFeatureStyles]);

  const syncSelectionList = useCallback(() => {
    setListTalhoesIds(new Set(selectedTalhoesRef.current));
  }, [setListTalhoesIds]);

  const scheduleSyncSelectionList = useCallback(() => {
    if (syncListTimeoutRef.current != null) return;
    syncListTimeoutRef.current = window.setTimeout(() => {
      syncListTimeoutRef.current = null;
      syncSelectionList();
    }, 0);
  }, [syncSelectionList]);

  const clearOsEditStateIfNeeded = useCallback(() => {
    if (activeEdit) return;
    const state = useModalOrdemServicoMatriz.getState();
    if (
      state.selectedItem == null &&
      !state.editChecked &&
      !state.ordemService?.code
    ) {
      return;
    }
    if (state.selectedItem != null) setSelectedItem(null);
    if (state.editChecked) setEditChecked(false);
    if (state.ordemService?.code) {
      setOrdemService({ code: '', date: '', team: '', operation: '', possuiPlanoOperacional: false });
    }
  }, [activeEdit, setSelectedItem, setEditChecked, setOrdemService]);

  const ensureOrdemTalhoesInSelectionSet = useCallback(() => {
    if (!ordemTalhaoIds?.length) return;
    ordemTalhaoIds.forEach((id: string) => {
      selectedTalhoesRef.current.add(id);
    });
  }, [ordemTalhaoIds]);

  useEffect(() => {
    if (!mapRef.current || !points) return;

    const map = mapRef.current;
    const styleFn = buildStyleFn();
    styleFnRef.current = styleFn;

    map.data.forEach((f: google.maps.Data.Feature) => map.data.remove(f));
    map.data.addGeoJson(points as object);
    map.data.revertStyle();
    map.data.setStyle(styleFn);
    rebuildFeatureIndex(map);

    boundsByIdRef.current = buildTalhaoBoundsIndex(
      (points.features ?? []) as Parameters<typeof buildTalhaoBoundsIndex>[0]
    );
    const codigoById = new Map<string, string>();
    for (const feature of points.features ?? []) {
      const id = feature.properties?.id;
      const codigo = feature.properties?.codigo;
      if (id != null && id !== "" && codigo) {
        codigoById.set(String(id), String(codigo));
      }
    }
    talhaoCodigoByIdRef.current = codigoById;

    clearSelectionState();
    setPopupPosition(null);
    setPopupTalhao(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- GeoJSON reload only when points change
  }, [points]);

  useEffect(() => {
    if (!mapRef.current || !points) return;

    const map = mapRef.current;
    const styleFn = buildStyleFn();
    styleFnRef.current = styleFn;
    const nextFeatures = new Map<string, google.maps.Data.Feature>();
    map.data.forEach((feature: google.maps.Data.Feature) => {
      const id = getFeatureTalhaoId(feature);
      const shouldSelect = !!(id && selectedTalhoesRef.current.has(id));
      try {
        feature.setProperty("selected", shouldSelect);
      } catch {
        // Feature may have been removed on GeoJSON reload.
      }
      if (shouldSelect && id) {
        nextFeatures.set(id, feature);
      }
    });
    selectedFeaturesRef.current = nextFeatures;

    map.data.revertStyle();
    map.data.setStyle(styleFn);
  }, [buildStyleFn, points]);

  useEffect(() => {
    if (!mapRef.current) return;

    if (ordemTalhaoIds && ordemTalhaoIds.length > 0) {
      if (!activeEdit) {
        setPopupPosition(null);
        setPopupTalhao(null);
        clearSelectionState();
      }
    }
  }, [ordemTalhaoIds, activeEdit, clearSelectionState]);

  useEffect(() => {
    return () => {
      if (syncListRafRef.current != null) {
        window.cancelAnimationFrame(syncListRafRef.current);
      }
      if (syncListTimeoutRef.current != null) {
        window.clearTimeout(syncListTimeoutRef.current);
      }
    };
  }, []);

  const addItemList = useCallback((
    feature: google.maps.Data.Feature,
    talhaoId: string,
    code: string,
    rightclick: boolean = false,
    includeOrderTalhoes = true,
  ) => {
    const alreadyInOS = feature.getProperty("ordemServicoIds")?.includes(ordemServicoId);

    const alreadySelected = selectedTalhoesRef.current.has(talhaoId);

    if (includeOrderTalhoes && activeEdit) {
      ensureOrdemTalhoesInSelectionSet();
    }

    if (alreadySelected) {
      if (!rightclick) {
        selectedTalhoesRef.current.delete(talhaoId);
        selectedCodesRef.current.delete(talhaoId);
        selectedFeaturesRef.current.delete(talhaoId);
      }
    } else {
      selectedTalhoesRef.current.add(talhaoId);
      if (code) selectedCodesRef.current.add(code);
      selectedFeaturesRef.current.set(talhaoId, feature);
    }

    if (!alreadyInOS) {
      applyTouchedSelectionStyles([feature]);
    }
    scheduleSyncSelectionList();
  }, [activeEdit, ensureOrdemTalhoesInSelectionSet, applyTouchedSelectionStyles, scheduleSyncSelectionList]);

  const handleBoxSelect = useCallback(
    (talhaoIds: string[]) => {
      if (!mapRef.current || talhaoIds.length === 0) return;
      if (editChecked && !activeEdit) return;

      const featuresById = featuresByIdRef.current;
      const codigoById = talhaoCodigoByIdRef.current;
      const touched: google.maps.Data.Feature[] = [];

      for (const talhaoId of talhaoIds) {
        const feature = featuresById.get(talhaoId);
        if (!feature) continue;

        touched.push(feature);
        const alreadySelected = selectedTalhoesRef.current.has(talhaoId);

        if (alreadySelected) {
          selectedTalhoesRef.current.delete(talhaoId);
          selectedCodesRef.current.delete(talhaoId);
          selectedFeaturesRef.current.delete(talhaoId);
        } else {
          selectedTalhoesRef.current.add(talhaoId);
          const code = codigoById.get(talhaoId);
          if (code) selectedCodesRef.current.add(code);
          selectedFeaturesRef.current.set(talhaoId, feature);
        }
      }

      if (activeEdit) {
        ensureOrdemTalhoesInSelectionSet();
      }
      clearOsEditStateIfNeeded();

      applyTouchedSelectionStyles(touched);
      scheduleSyncSelectionList();
    },
    [
      mapRef,
      editChecked,
      activeEdit,
      ensureOrdemTalhoesInSelectionSet,
      clearOsEditStateIfNeeded,
      applyTouchedSelectionStyles,
      scheduleSyncSelectionList,
    ]
  );

  const { boxRect, skipNextClickRef, getProjection } = useTalhaoBoxSelect({
    mapRef,
    boundsByIdRef,
    enabled: true,
    onSelect: handleBoxSelect,
  });

  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;

    const clickListener = map.data.addListener("click", (event: any) => {
      const osMatriz = useModalOrdemServicoMatriz.getState();
      const os = osMatriz.ordemService;
      const isEditing = osMatriz.activeEdit;
      if (os.code !== "" && !isEditing) return;
      if (skipNextClickRef.current) {
        skipNextClickRef.current = false;
        return;
      }
      skipNextClickRef.current = false;

      const feature = event.feature as google.maps.Data.Feature;
      const talhaoId = getFeatureTalhaoId(feature);
      const code = feature.getProperty("codigo");

      if (!talhaoId) {
        clearSelectionState();
        setListTalhoesIds(new Set());
        setOrdemServicoIdAndColor({
          ...ordemServicoIdAndColor,
          talhaoIds: [],
          codesAndArea: [],
        });
        setPopupPosition(null);
        setPopupTalhao(null);
        scheduleSyncSelectionList();
        return;
      }

      const isCtrlPressed = event.domEvent?.ctrlKey;

      if (!isCtrlPressed) {
        clearSelectionState();
        setListTalhoesIds(new Set());
        setOrdemServicoIdAndColor({
          ...ordemServicoIdAndColor,
          talhaoIds: [],
          codesAndArea: [],
        });
      }

      clearOsEditStateIfNeeded();
      addItemList(feature, talhaoId, code, false, true);
    });

    return () => {
      google.maps.event.removeListener(clickListener);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeEdit]);

  useEffect(() => {
    if (!points) return;

    const container = document.getElementById("map-print-area");
    if (!container) return;

    function openContextMenuAt(
      latLng: google.maps.LatLng,
      preferredFeature?: google.maps.Data.Feature | null
    ) {
      if (selectedTalhoesRef.current.size === 0 && preferredFeature) {
        const talhaoId = getFeatureTalhaoId(preferredFeature);
        const code = preferredFeature.getProperty("codigo");
        if (talhaoId) {
          addItemList(preferredFeature, talhaoId, code, true);
        }
      }

      if (selectedTalhoesRef.current.size === 0) return;

      clearOsEditStateIfNeeded();
      // Flush selection to store before opening menu / Criar O.S.
      syncSelectionList();

      setPopupPosition({
        lat: latLng.lat(),
        lng: latLng.lng(),
      });

      let targetFeature = preferredFeature ?? null;
      if (!targetFeature) {
        const selectedId = selectedTalhoesRef.current.values().next().value as string | undefined;
        if (selectedId) {
          targetFeature = selectedFeaturesRef.current.get(selectedId)
            ?? featuresByIdRef.current.get(selectedId)
            ?? null;
        }
      }

      if (!targetFeature) {
        const fallbackId = selectedTalhoesRef.current.values().next().value as string | undefined;
        setPopupTalhao({ id: fallbackId } as unknown as CustomFeature);
        return;
      }

      const props: Record<string, unknown> = {};
      try {
        targetFeature.forEachProperty((value: unknown, key: string) => {
          props[key] = value;
        });
      } catch {
        const talhaoId = getFeatureTalhaoId(targetFeature);
        if (talhaoId) props.id = talhaoId;
      }

      setPopupTalhao(props as unknown as CustomFeature);
    }

    function handleContextMenu(event: MouseEvent) {
      if (ordemService.code !== "" && !activeEdit) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest?.("[data-talhao-context-menu]")) return;

      event.preventDefault();
      event.stopPropagation();

      const map = mapRef.current;
      const projection = getProjection();
      if (!map || !projection) return;

      const mapDiv = map.getDiv() as HTMLElement;
      const rect = mapDiv.getBoundingClientRect();
      const latLng = projection.fromContainerPixelToLatLng(
        new google.maps.Point(event.clientX - rect.left, event.clientY - rect.top)
      );
      if (!latLng) return;

      if (selectedTalhoesRef.current.size > 0) {
        openContextMenuAt(latLng, null);
        return;
      }

      let hitFeature: google.maps.Data.Feature | null = null;
      map.data.forEach((feature: google.maps.Data.Feature) => {
        if (hitFeature) return;
        if (!getFeatureTalhaoId(feature)) return;
        const geometry = feature.getGeometry();
        if (!geometry) return;

        const type = geometry.getType();
        if (type === "Polygon") {
          const ring = (geometry as google.maps.Data.Polygon).getAt(0);
          const polygon = new google.maps.Polygon({ paths: ring.getArray() });
          if (google.maps.geometry.poly.containsLocation(latLng, polygon)) {
            hitFeature = feature;
          }
          return;
        }

        if (type === "MultiPolygon") {
          const multi = geometry as google.maps.Data.MultiPolygon;
          const matched = multi.getArray().some((poly: google.maps.Data.Polygon) => {
            const outer = poly.getAt(0);
            const polygon = new google.maps.Polygon({ paths: outer.getArray() });
            return google.maps.geometry.poly.containsLocation(latLng, polygon);
          });
          if (matched) hitFeature = feature;
        }
      });

      if (!hitFeature) {
        setPopupPosition(null);
        setPopupTalhao(null);
        return;
      }

      openContextMenuAt(latLng, hitFeature);
    }

    container.addEventListener("contextmenu", handleContextMenu, true);
    return () => {
      container.removeEventListener("contextmenu", handleContextMenu, true);
    };
  }, [
    points,
    getProjection,
    mapRef,
    activeEdit,
    addItemList,
    clearOsEditStateIfNeeded,
    syncSelectionList,
  ]);

  const resetClick = useCallback((option: boolean = false) => {
    setPopupPosition(null);
    setPopupTalhao(null);

    if (!option) {
      // Keep selected ids for Criar/Editar O.S., only hide selection highlight.
      revertSelectedFeatureStyles();
      return;
    }

    setSelectedItem(null);
    clearSelectionState();
    setEditChecked(false);
    setActiveEdit(false);
    setOrdemService({ code: '', date: '', team: '', operation: '', possuiPlanoOperacional: false });
    setOrdemServicoIdAndColor({
      ordemServicoId: '',
      color: '',
      talhaoIds: [],
      codesAndArea: []
    });
    syncSelectionList();
  }, [
    clearSelectionState,
    revertSelectedFeatureStyles,
    setSelectedItem,
    setEditChecked,
    setActiveEdit,
    setOrdemService,
    setOrdemServicoIdAndColor,
    syncSelectionList,
  ]);

  const menuProjection = getProjection();
  const menuPixel = popupPosition && menuProjection
    ? menuProjection.fromLatLngToContainerPixel(
      new google.maps.LatLng(popupPosition.lat, popupPosition.lng)
    )
    : null;
  const mapContainer = mapRef.current?.getDiv() ?? null;

  return (
    <>
      <BoxSelectOverlay boxRect={boxRect} />
      {isOrdemServicoOpen && (
        <OrdemServico
          planoOperacionalData={planoOperacionalData}
          onClose={() => {
            setIsOrdemServicoOpen(false);
            resetClick(true);
          }}
          listMap={Polygons}
          isEdit={activeEdit && editChecked}
          ordemServico={{ id: ordemServicoIdAndColor.ordemServicoId, code: Number(ordemService.code) }}
        />
      )}
      <MarkersContainer>
        <TalhaoLabels
          map={mapRef.current}
          items={Polygons}
          visible={
            displayMarkers &&
            currentViewType !== CurrentViewTypeEnum.transparent
          }
          currentViewType={currentViewType}
        />
        {(!activeEdit || (activeEdit && editChecked)) &&
          selectedTalhoesRef.current.size > 0 &&
          popupTalhao &&
          menuPixel &&
          mapContainer &&
          createPortal(
            <ContextMenu
              data-talhao-context-menu
              style={{
                position: "absolute",
                left: menuPixel.x,
                top: menuPixel.y,
                zIndex: 1001,
              }}
            >
              <ContextMenuItem onClick={(e) => {
                e.stopPropagation();
                setIsOrdemServicoOpen(true);
                resetClick();
              }}>
                <MdAddCircle size={20} />
                {activeEdit && editChecked ? "Editar O.S." : "Criar O.S."}
              </ContextMenuItem>
              <ContextMenuItem onClick={(e) => {
                e.stopPropagation();
                resetClick(true);
              }}>
                <MdClear size={20} /> Cancelar seleção
              </ContextMenuItem>
            </ContextMenu>,
            mapContainer
          )}
      </MarkersContainer>
    </>
  );
}

export default BaseMap;
